import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import VMasker from 'vanilla-masker';

import { Grid, Typography, FormControlLabel, Switch } from '@material-ui/core';
import { Formik, Form } from 'formik';
import Card from 'components/Card';
import Loader from 'components/Loader';
import WarningMessage from 'components/WarningMessage';
import Input from 'components/Input';
import Select from 'components/Select';
import Button from 'components/Button';

import { loadConfigurations, upsertConfigurations } from '../../store/thunk';

import { availableWithdrawTimes } from '../resources';

import { checkedValueByType } from 'helpers/common';
import { convertToReal, convertFormattedMoneyToNumber } from 'helpers/converters';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

export default function Cashier({ formikProps }) {
  const dispatch = useDispatch();

  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const location = useLocation();
  const isRegistration = location.pathname == '/cadastro-inicial/caixa';

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { isLoading } = useSelector(state => state.operational);
  const { settingHistoryUserName, settingHistoryDateTime } = useSelector(state => state.operational);
  const cashier = useSelector(state => state.operational.cashier);

  useEffect(() => {
    if(establishmentId) {
      dispatch(loadConfigurations(userId, establishmentId));
    }
  }, [establishmentId]);

  const hasOperationalCashReserveChanged = (cashReserve, operationalCashReserve) => {
    return VMasker.toMoney(cashReserve) != VMasker.toMoney(operationalCashReserve);
  }

  const handleSubmitConfigurations = values => {
    let params = {
      closeCashierKeepMoney: checkedValueByType(values?.closeCashierKeepMoney, 'number'),
      requiredOpenedCashierForVehicleEntry: checkedValueByType(values?.requiredOpenedCashierForVehicleEntry, 'number'),
      withdrawal: values?.withdrawal,
      page: 'Caixa'
    }

    if(hasOperationalCashReserveChanged(cashier?.cashReserve, values?.cashReserve)) {
      params = {
        ...params,
        cashReserve: convertFormattedMoneyToNumber(values?.cashReserve)
      }
    }

    if(values?.closeCashierKeepMoney) {
      delete params['cashReserve'];
    }

    dispatch(upsertConfigurations(userId, establishmentId, params));
  }

  const configurationInitialValues = {
    closeCashierKeepMoney: checkedValueByType(cashier?.closeCashierKeepMoney, 'boolean'),
    requiredOpenedCashierForVehicleEntry: checkedValueByType(cashier?.requiredOpenedCashierForVehicleEntry, 'boolean'),
    cashReserve: cashier?.cashReserve,
    withdrawal: cashier?.withdrawal?.toString()
  }

  return(
    <Card title="Configurações de Caixa">
      <Grid xs={12} alignItems='center'>
        <Loader isLoading={isLoading}>
          <Formik
            enableReinitialize
            initialValues={configurationInitialValues}
            onSubmit={handleSubmitConfigurations}
          >
            {({ ...formikNewProps }) => {
              return(
                <Form>
                  <Grid container>
                    {(!!settingHistoryUserName && !!settingHistoryDateTime) && (
                      <WarningMessage
                        message={`Última alteração feita por ${settingHistoryUserName} em ${moment(settingHistoryDateTime).format('LLL')}`}
                      />
                    )}
                    <Grid item xs={12} style={{ marginTop: 10 }}>
                      <Typography color="primary" style={{ borderBottom: '1px solid #E4E7EA' }}>
                        Fechamento de caixa gera um saldo em dinheiro que pode ser aproveitado para <strong>abertura do próximo caixa</strong>
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            name="closeCashierKeepMoney"
                            color="primary"
                            value={formikNewProps.values?.closeCashierKeepMoney || formikProps?.values?.closeCashierKeepMoney || ''}
                            checked={formikNewProps.values?.closeCashierKeepMoney || formikProps?.values?.closeCashierKeepMoney || ''}
                            onChange={event => {
                              formikNewProps.setFieldValue(event.target.name, event.target.checked);

                              if(formikProps) {
                                formikProps.setFieldValue(event.target.name, event.target.checked);
                              }
                            }}
                          />
                        }
                        label={
                          <Typography color="textSecondary">Obrigar</Typography>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} style={{ marginTop: 10 }}>
                      <Typography color="primary" style={{ borderBottom: '1px solid #E4E7EA' }}>
                        Obrigar o <strong>caixa estar aberto</strong> para dar entrada num veículo
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            name="requiredOpenedCashierForVehicleEntry"
                            color="primary"
                            value={formikNewProps.values?.requiredOpenedCashierForVehicleEntry || formikProps?.values?.requiredOpenedCashierForVehicleEntry || ''}
                            checked={formikNewProps.values?.requiredOpenedCashierForVehicleEntry || formikProps?.values?.requiredOpenedCashierForVehicleEntry || ''}
                            onChange={event => {
                              formikNewProps.setFieldValue(event.target.name, event.target.checked);

                              if(formikProps) {
                                formikProps.setFieldValue(event.target.name, event.target.checked);
                              }
                            }}
                          />
                        }
                        label={
                          <Typography color="textSecondary">Obrigar</Typography>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} style={{ marginTop: 10 }}>
                      <Typography color="primary" style={{ borderBottom: '1px solid #E4E7EA' }}>
                        <b>Escolha o valor da reserva de caixa</b>
                      </Typography>
                      <Typography color="textSecondary" style={{ margin: '10px 0 10px 0' }}>
                        Configure a quantidade mínima sugerida de dinheiro que o estabelecimento precisa manter no caixa.
                      </Typography>
                      <Input
                        fullWidth
                        name="cashReserve"
                        type="money"
                        label="Reserva de caixa"
                        disabled={formikNewProps.values?.closeCashierKeepMoney || formikProps?.values?.closeCashierKeepMoney}
                        placeholder="R$ 0,00"
                        value={convertToReal(convertFormattedMoneyToNumber(formikNewProps.values?.cashReserve)) || convertToReal(convertFormattedMoneyToNumber(formikProps?.values?.cashReserve)) || ''}
                        onChange={event => {
                          formikNewProps.setFieldValue(event.target.name, event.target.value);

                          if(formikProps) {
                            formikProps.setFieldValue(event.target.name, event.target.value);
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container style={{ marginTop: 20 }}>
                    <Grid item xs={12}>
                      <Typography color="primary" style={{ borderBottom: '1px solid #E4E7EA' }}>
                        <b>Escolha a frequência do fechamento de caixa</b>
                      </Typography>
                      <Typography color="textSecondary" style={{ margin: '10px 0 10px 0' }}>
                        Todo dia, automaticamente, o sistema fecha o caixa a meia noite, porém você pode ignorar essa automatização e fechar o caixa a qualquer momento quando fizer uma sangria.
                      </Typography>
                      <Select
                        name="withdrawal"
                        label="Selecione a frequência"
                        options={availableWithdrawTimes}
                        value={formikNewProps.values?.withdrawal?.toString() || formikProps?.values?.withdrawal?.toString() || ''}
                        onChange={event => {
                          formikNewProps.setFieldValue(event.target.name, event.target.value);

                          if(formikProps) {
                            formikProps.setFieldValue(event.target.name, event.target.value);
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                  {!isRegistration && (
                    <Grid container xs={12} justify='flex-end' style={{ marginTop: 10 }}>
                      <Button
                        fullWidth={isSm}
                        type="submit"
                        color="success"
                      >
                        Salvar
                      </Button>
                    </Grid>
                  )}
                </Form>
              );
            }}
          </Formik>
        </Loader>
      </Grid>
    </Card>
  );
}